import { getToken } from './api.ts';
import { ensureNoteOnServer } from './sync.ts';
import { fromB64, newDataKey, openBytes, sealBytes, toB64 } from '../crypto/vault.ts';
import { store } from './db.ts';
import { flushPersist, withDoc } from './docs.ts';
import { newId } from './notes.ts';

/**
 * Anhänge (docs/KONZEPT.md, Abschnitt 11).
 *
 * Jede Datei bekommt einen eigenen Zufallsschlüssel. Verschlüsselt wird im Browser,
 * hochgeladen wird nur der Ciphertext; Schlüssel, Dateiname und Typ leben im
 * Notizdokument und damit im selben CRDT wie der Text. Der Server sieht eine Zahl von
 * Bytes und sonst nichts.
 *
 * Abweichung vom Konzept: Die Schlüssel stehen nicht im Frontmatter, sondern in einer
 * eigenen Y.Map. Im Frontmatter wären sie sichtbar, versehentlich löschbar und
 * würden in Vorschau und Suche auftauchen.
 */

export type AttachmentMeta = {
  name: string;
  mime: string;
  size: number;
  /** Dateischlüssel, base64. Liegt im verschlüsselten Notizdokument. */
  key: string;
};

/**
 * Bildbehandlung vor dem Hochladen. WebP allein spart bei Bildschirmfotos gut die
 * Hälfte, auch ohne zu verkleinern — deshalb wird jedes Bild neu kodiert und nur
 * behalten, was tatsächlich kleiner ist.
 */
export type ImageMode = 'small' | 'balanced' | 'original';

const MODES: Record<ImageMode, { edge: number | null; quality: number }> = {
  small: { edge: 1600, quality: 0.8 },
  balanced: { edge: 2560, quality: 0.85 },
  original: { edge: null, quality: 1 },
};

/** Animation überlebt den Weg über die Leinwand nicht, Vektoren verlieren dabei nur. */
const UNTOUCHED = new Set(['image/gif', 'image/svg+xml', 'image/avif']);

const MAX_BYTES = 100 * 1024 * 1024;

const blobUrls = new Map<string, string>();
const loading = new Map<string, Promise<string | null>>();

export function attachmentUrl(id: string): string | null {
  return blobUrls.get(id) ?? null;
}

async function readMeta(noteId: string, id: string): Promise<AttachmentMeta | null> {
  return withDoc(noteId, (entry) => {
    const raw = entry.doc.getMap('files').get(id);
    return typeof raw === 'string' ? (JSON.parse(raw) as AttachmentMeta) : null;
  });
}

async function writeMeta(noteId: string, id: string, meta: AttachmentMeta): Promise<void> {
  await withDoc(noteId, (entry) => {
    entry.doc.getMap('files').set(id, JSON.stringify(meta));
  });
  // Sofort schreiben, nicht in 250 Millisekunden: Der Eintrag ist der einzige Weg
  // zurück zur hochgeladenen Datei. Geht er in einer Sammelverzögerung verloren,
  // liegen die Bytes auf dem Server und niemand findet sie je wieder — ohne Fehler,
  // ohne Meldung, ohne Möglichkeit, es zu bemerken.
  await flushPersist(noteId);
}

export type Prepared = {
  bytes: Uint8Array;
  mime: string;
  name: string;
  /** Ursprüngliche Größe, damit die Oberfläche die Ersparnis zeigen kann. */
  originalSize: number;
};

/**
 * Verkleinert und rekodiert Bilder. Behalten wird nur, was wirklich kleiner ist —
 * ein bereits gut komprimiertes Foto wird durch eine zweite Runde sonst größer.
 */
export async function prepare(file: File, mode?: ImageMode): Promise<Prepared> {
  const original = new Uint8Array(await file.arrayBuffer());
  const untouched: Prepared = {
    bytes: original,
    mime: file.type || 'application/octet-stream',
    name: file.name,
    originalSize: original.byteLength,
  };
  if (!file.type.startsWith('image/') || UNTOUCHED.has(file.type)) return untouched;

  const settings = MODES[mode ?? (await store.getImageMode())];
  if (settings.edge === null) return untouched;

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return untouched;

  const longest = Math.max(bitmap.width, bitmap.height);
  const scale = longest > settings.edge ? settings.edge / longest : 1;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return untouched;
  }
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', settings.quality),
  );
  if (!blob || blob.size >= original.byteLength) return untouched;

  return {
    bytes: new Uint8Array(await blob.arrayBuffer()),
    mime: 'image/webp',
    name: `${file.name.replace(/\.[^.]+$/, '')}.webp`,
    originalSize: original.byteLength,
  };
}

export class TooLarge extends Error {
  constructor(readonly bytes: number) {
    super('Datei ist zu groß');
    this.name = 'TooLarge';
  }
}

/** Verschlüsselt und lädt hoch. Ohne Notiz-Bezug — den stellt registerAttachment her. */
export async function uploadEncrypted(
  bytes: Uint8Array,
  mime: string,
  name: string,
  noteId: string,
): Promise<{ id: string; meta: AttachmentMeta }> {
  if (bytes.byteLength > MAX_BYTES) throw new TooLarge(bytes.byteLength);
  // Der Anhang hängt an der Notizzeile; die muss der Server kennen.
  await ensureNoteOnServer(noteId);

  const id = newId();
  const key = newDataKey();
  const sealed = sealBytes(bytes, key);

  /**
   * Drei Anläufe. Ein paar Megabyte über eine Mobilverbindung reißen leicht ab, und
   * ein einzelner Versuch lässt dann ein Dokument ohne seine Datei zurück. Bei einer
   * Absage des Servers (4xx) wird nicht wiederholt — die wird beim zweiten Mal
   * genauso ausfallen.
   */
  let letzter = '';
  for (let versuch = 1; versuch <= 3; versuch += 1) {
    try {
      const response = await fetch(`/api/attachments/${id}?note=${noteId}`, {
        method: 'PUT',
        headers: {
          authorization: `Bearer ${getToken()}`,
          'content-type': 'application/octet-stream',
        },
        body: sealed as BodyInit,
      });
      if (response.ok) break;
      letzter = `Der Server hat abgelehnt (${response.status}${
        response.status === 413 ? ', Datei zu groß' : ''
      })`;
      if (response.status < 500) throw new Error(`Upload fehlgeschlagen: ${letzter}`);
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('Upload fehlgeschlagen')) throw error;
      letzter = 'Die Verbindung ist abgerissen';
    }
    if (versuch === 3) throw new Error(`Upload fehlgeschlagen: ${letzter}`);
    await new Promise((weiter) => setTimeout(weiter, versuch * 1_000));
  }

  blobUrls.set(id, URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime })));
  return { id, meta: { name, mime, size: bytes.byteLength, key: toB64(key) } };
}

export async function registerAttachment(
  noteId: string,
  id: string,
  meta: AttachmentMeta,
): Promise<void> {
  await writeMeta(noteId, id, meta);
}

export function attachmentMarkdown(id: string, meta: AttachmentMeta): string {
  const label = meta.name.replace(/[[\]]/g, '');
  return meta.mime.startsWith('image/')
    ? `![${label}](athene:${id})`
    : `[${label}](athene:${id})`;
}

/** Lädt eine Datei hoch und gibt das Markdown zurück, das sie einbindet. */
export async function attachFile(
  noteId: string,
  file: File,
): Promise<{ id: string; markdown: string; saved: number }> {
  const prepared = await prepare(file);
  const { id, meta } = await uploadEncrypted(prepared.bytes, prepared.mime, prepared.name, noteId);
  await registerAttachment(noteId, id, meta);
  return {
    id,
    markdown: attachmentMarkdown(id, meta),
    saved: prepared.originalSize - prepared.bytes.byteLength,
  };
}

/**
 * Holt den Blob, entschlüsselt ihn im Browser und gibt eine Blob-URL zurück. Ergebnisse
 * bleiben im Speicher, damit ein Neuzeichnen nicht jedes Mal neu entschlüsselt.
 */
export function ensureAttachment(noteId: string, id: string): Promise<string | null> {
  const existing = blobUrls.get(id);
  if (existing) return Promise.resolve(existing);
  const running = loading.get(id);
  if (running) return running;

  const task = (async (): Promise<string | null> => {
    try {
      const meta = await readMeta(noteId, id);
      if (!meta) return null;
      const response = await fetch(`/api/attachments/${id}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) return null;
      const bytes = openBytes(new Uint8Array(await response.arrayBuffer()), fromB64(meta.key));
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: meta.mime }));
      blobUrls.set(id, url);
      return url;
    } catch (error) {
      console.warn('[anhang] nicht ladbar', id, error);
      return null;
    } finally {
      loading.delete(id);
    }
  })();
  loading.set(id, task);
  return task;
}

export async function attachmentBytes(
  noteId: string,
  id: string,
): Promise<{ meta: AttachmentMeta; bytes: Uint8Array } | null> {
  const meta = await readMeta(noteId, id);
  if (!meta) return null;
  const response = await fetch(`/api/attachments/${id}`, {
    headers: { authorization: `Bearer ${getToken()}` },
  });
  if (!response.ok) return null;
  return { meta, bytes: openBytes(new Uint8Array(await response.arrayBuffer()), fromB64(meta.key)) };
}

export async function listAttachments(noteId: string): Promise<Record<string, AttachmentMeta>> {
  return withDoc(noteId, (entry) => {
    const out: Record<string, AttachmentMeta> = {};
    for (const [id, raw] of entry.doc.getMap('files').entries()) {
      if (typeof raw === 'string') out[id] = JSON.parse(raw) as AttachmentMeta;
    }
    return out;
  });
}

/** Der Anzeigename für ein noch nicht geladenes oder fehlendes Bild. */
export function metaOf(noteId: string, id: string): Promise<AttachmentMeta | null> {
  return readMeta(noteId, id);
}
